// A web screen inside the iPhone app whose session check could not reach
// the server (offline, a 5xx, a proxy's error page). The login form would
// ask someone who is signed in to sign in again, inside a web view the app
// signs in by itself (spec §6), so the page says what happened and checks
// again: soon at first, then every half minute (retryDelayMs), at once when
// the phone comes back online or the screen is shown again, and on "Try
// again". A 401 on any retry is a real sign-out: App renders the login
// form, which tells the app (useNativeSignedOut). The app hears {type:
// "offline"} once per showing and may draw its own retry screen instead.

import { useCallback, useEffect, useRef, useState } from "react";
import { WifiOff } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { useAuth } from "@/stores/auth";
import { ambientLight } from "@/theme";

import { postToNative, retryDelayMs } from "./embedded";

export function EmbeddedOffline() {
  const rehydrate = useAuth((s) => s.rehydrate);
  const [attempt, setAttempt] = useState(0);
  const [checking, setChecking] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const told = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Once per showing: StrictMode runs the effect twice.
  useEffect(() => {
    if (told.current) return;
    told.current = true;
    postToNative({ type: "offline" });
  }, []);

  const retry = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setChecking(true);
    try {
      // Success or a 401 replaces this screen (App re-renders from the store).
      await rehydrate();
    } finally {
      busy.current = false;
      if (mounted.current) {
        setChecking(false);
        setAttempt((n) => n + 1);
      }
    }
  }, [rehydrate]);

  useEffect(() => {
    const timer = window.setTimeout(() => void retry(), retryDelayMs(attempt));
    return () => window.clearTimeout(timer);
  }, [attempt, retry]);

  useEffect(() => {
    const now = () => void retry();
    const onVisible = () => {
      if (document.visibilityState === "visible") now();
    };
    window.addEventListener("online", now);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", now);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [retry]);

  return (
    <div
      className="flex app-vh w-full flex-col items-center justify-center overflow-y-auto px-4 pt-safe pb-safe"
      style={{ background: ambientLight }}
    >
      <main className="flex w-full max-w-[432px] flex-col items-center text-center" role="status" aria-live="polite">
        <WifiOff className="h-8 w-8 text-fg-subtle" aria-hidden />
        <h1 className="mt-4 font-display text-xl tracking-[var(--font-display-tracking)] text-fg">
          Can&apos;t reach Corvus
        </h1>
        <p className="mt-1 text-base text-fg-muted">
          {checking ? "Trying again…" : "Check your connection. This page will try again by itself."}
        </p>
        <Button className="mt-6" size="lg" onClick={() => void retry()} loading={checking}>
          Try again
        </Button>
      </main>
    </div>
  );
}
